export const ATTICUS_BROWSER_API = '/api/integrations/atticus';
export const ATTICUS_ROUTE_BASE = '/account/integrations/atticus';
export const isAtticusIdentifier = (value: string | undefined): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{16,100}$/.test(value);

/** Only canonical local quote routes may be used after purchasing credits. */
export const atticusQuoteReturnPath = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const match = /^\/account\/integrations\/atticus\/quotes\/([A-Za-z0-9_-]{16,100})$/.exec(value);
  return match ? `${ATTICUS_ROUTE_BASE}/quotes/${match[1]}` : undefined;
};

export const atticusPurchasePath = (
  quoteId: string,
  required: number,
  available: number
): string => {
  if (!isAtticusIdentifier(quoteId)) throw new Error('Invalid quote identifier.');
  const params = new URLSearchParams({
    openTopUp: '1',
    source: 'atticus',
    requiredCredits: String(required),
    availableCredits: String(available),
    returnTo: `${ATTICUS_ROUTE_BASE}/quotes/${quoteId}`,
  });
  return `/account/billing?${params}`;
};

/** Firebase headers are never attached to this browser navigation. */
export const navigateToAtticusCallback = (value: unknown): void => {
  if (typeof value !== 'string') throw new Error('Invalid authorization response.');
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password)
    throw new Error('Invalid authorization response.');
  window.location.assign(url.href);
};

export const atticusError = (error: any): string => {
  const code = error?.response?.data?.error?.code;
  const messages: Record<string, string> = {
    invalid_authorization_intent:
      'This connection request is invalid or has expired. Start again from Atticus.',
    invalid_user_grant: 'This Atticus connection is unavailable. Reconnect from Atticus.',
    grant_revoked: 'This connection was disconnected. Reconnect from Atticus.',
    quote_expired: 'This quote has expired. Request a fresh quote in Atticus.',
    source_changed: 'The book has changed. Return to Atticus and request a fresh quote.',
    quote_changed: 'The translation terms have changed. Request a fresh quote in Atticus.',
    insufficient_credits:
      'Your available credits no longer cover this quote. Refresh the balance or purchase credits.',
    job_conflict: 'This translation has already been submitted. Check its progress in Atticus.',
    not_found: 'This request is unavailable for the signed-in account.',
    rate_limited: 'Please wait a moment and try again.',
    service_unavailable: 'The Atticus integration is unavailable right now. Please try again.',
  };
  return (
    messages[code] ||
    (error?.response?.status === 403
      ? 'This account cannot access this Atticus request.'
      : 'The request could not be completed. Please try again.')
  );
};
